/**
 * Design Lab Init Machine Guards
 * All guard conditions for state transitions
 */

import type { DesignLabInitContext } from "./design-lab-init.types";

export const guards = {
  // Flow Type Guards
  isEditMode: ({ context }: { context: DesignLabInitContext }) => !!context.urlParams.asgid,

  isExistingProject: ({ context }: { context: DesignLabInitContext }) => !!context.urlParams.uuid,

  isAutoCreate: ({ context }: { context: DesignLabInitContext }) =>
    // Auto-create if we have upload sources or preselected choice
    !!(context.urlParams.choice || context.urlParams.artworkUrls || context.urlParams.uploadUrls),
  // Composition Guards
  hasCompositionParams: ({ context }: { context: DesignLabInitContext }) =>
    // Check if we have a composition that needs to be applied
    !!context.composition,
  // Project Guards
  hasProject: ({ context }: { context: DesignLabInitContext }) => !!context.project,

  hasLatestProject: ({ context }: { context: DesignLabInitContext }) => !!context.latestProject,

  // Upload Source Guards
  hasUploadSource: ({ context }: { context: DesignLabInitContext }) => !!context.uploadSource,

  isExternalUpload: ({ context }: { context: DesignLabInitContext }) =>
    context.uploadSource === "external",

  isLibraryUpload: ({ context }: { context: DesignLabInitContext }) =>
    context.uploadSource === "library",

  isCanvaUpload: ({ context }: { context: DesignLabInitContext }) =>
    context.uploadSource === "canva",

  isDeviceUpload: ({ context }: { context: DesignLabInitContext }) =>
    context.uploadSource === "device",

  // File Guards
  hasUploadedFiles: ({ context }: { context: DesignLabInitContext }) =>
    !!(context.uploadedFiles && context.uploadedFiles.length > 0),

  hasExternalUrls: ({ context }: { context: DesignLabInitContext }) =>
    !!(context.externalUrls && context.externalUrls.length > 0),

  // Modal Guards
  isModalFlow: ({ context }: { context: DesignLabInitContext }) => context.flowType === "modal",

  shouldShowModal: ({ context }: { context: DesignLabInitContext }) =>
    // Show modal if no direct action specified
    !(
      context.urlParams.asgid ||
      context.urlParams.uuid ||
      context.urlParams.choice ||
      context.urlParams.artworkUrls ||
      context.urlParams.uploadUrls
    ),
  // Validation Guards
  isValidComposition: ({ context }: { context: DesignLabInitContext }) => {
    if (!context.composition) return false;

    // Validate composition has required fields
    return !!(context.composition.catalogNumber && context.composition.ac);
  },

  isValidProject: ({ context }: { context: DesignLabInitContext }) => {
    if (!context.project) return false;

    // Validate project has required fields
    return !!context.project.uuid;
  },

  // PDP Data Guards
  hasPdpSizeData: ({ context }: { context: DesignLabInitContext }) =>
    !!(context.composition?.pdpSizeData && Object.keys(context.composition.pdpSizeData).length > 0),

  // Error Guards
  hasError: ({ context }: { context: DesignLabInitContext }) => !!context.error,

  // Combined Guards for Complex Conditions
  shouldAddCompositionToExisting: ({ context }: { context: DesignLabInitContext }) =>
    // Add composition if we loaded an existing project and have composition params
    !!(context.flowType === "existing" && context.project && context.composition),
  shouldCreateWithComposition: ({ context }: { context: DesignLabInitContext }) =>
    // Create with composition if auto-create flow has composition params
    !!(context.flowType === "create" && context.composition),
  shouldProcessExternalUpload: ({ context }: { context: DesignLabInitContext }) =>
    // Process external upload if we have URLs
    !!(
      context.uploadSource === "external" &&
      context.externalUrls &&
      context.externalUrls.length > 0
    ),
  isReadyToComplete: ({ context }: { context: DesignLabInitContext }) =>
    // Check if we're ready to complete initialization
    !!(context.project && !context.error && !context.showModal)
};
